/**
 * Calendário de requisições — compartilhado por Compra e Separação.
 *
 * Por padrão marca o dia de CRIAÇÃO com a cor da situação (Separação).
 * Compras (ADR-0069) passa `eventosDe(req) => [{ data, tone, rotulo }]` e
 * `legenda` para mostrar prazos de cotação, entregas previstas e atrasos —
 * o que o comprador precisa ver num mês, não quando o pedido nasceu.
 */
import React, { useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import { getToneTokens } from "../../../../styles/shared";
import { CalCell, CalChip, CalDay, CalGrid, CalHead, CalWrap } from "./styles";

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MAX_POR_DIA = 3;

function chaveDia(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "2026-09-18" (data pura) vira meia-noite LOCAL — nunca `new Date(iso)`,
 *  que interpreta como UTC e cai no dia anterior em São Paulo. */
function dataLocal(valor) {
  if (!valor) return null;
  if (valor instanceof Date) return valor;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(valor);
}

function eventosPadrao(r, vocab) {
  return [{ data: r.created_at, tone: vocab.SITUACAO_TONE[r.situacao], rotulo: r.cliente_nome || r.numero }];
}

export default function Calendario({ requisicoes, onAbrir, vocab, eventosDe, legenda }) {
  const [ref, setRef] = useState(() => new Date());

  const porDia = useMemo(() => {
    const map = new Map();
    requisicoes.forEach((r) => {
      const evs = eventosDe ? eventosDe(r) : eventosPadrao(r, vocab);
      evs.forEach((ev) => {
        const d = dataLocal(ev.data);
        if (!d) return;
        const k = chaveDia(d);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push({ ...ev, req: r });
      });
    });
    return map;
  }, [requisicoes, eventosDe, vocab]);

  const { celulas, titulo } = useMemo(() => {
    const ano = ref.getFullYear();
    const mes = ref.getMonth();
    const primeiro = new Date(ano, mes, 1);
    const inicio = new Date(primeiro);
    inicio.setDate(1 - primeiro.getDay()); // volta ao domingo da 1a semana
    const cels = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(inicio);
      d.setDate(inicio.getDate() + i);
      cels.push(d);
    }
    return {
      celulas: cels,
      titulo: ref.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    };
  }, [ref]);

  function irMes(delta) {
    setRef((r) => new Date(r.getFullYear(), r.getMonth() + delta, 1));
  }

  const mesAtual = ref.getMonth();
  const hojeK = chaveDia(new Date());

  return (
    <CalWrap>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button className="btn btn--secondary btn--sm" onClick={() => irMes(-1)} aria-label="Mês anterior">
          <FiChevronLeft size={14} />
        </button>
        <strong style={{ textTransform: "capitalize", minWidth: 150, textAlign: "center", fontSize: 14 }}>{titulo}</strong>
        <button className="btn btn--secondary btn--sm" onClick={() => irMes(1)} aria-label="Próximo mês">
          <FiChevronRight size={14} />
        </button>
        <button className="btn btn--secondary btn--sm" onClick={() => setRef(new Date())}>Hoje</button>
        {legenda?.length ? (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "var(--ink-3)" }}>
            {legenda.map((l) => (
              <span key={l.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: getToneTokens(null, l.tone).accent }} />
                {l.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <CalGrid>
        {DIAS.map((d) => <CalHead key={d}>{d}</CalHead>)}
        {celulas.map((d) => {
          const doMes = d.getMonth() === mesAtual;
          const k = chaveDia(d);
          const itens = porDia.get(k) || [];
          return (
            <CalCell key={d.toISOString()} $muted={!doMes}>
              <CalDay $hoje={k === hojeK}>{d.getDate()}</CalDay>
              {itens.slice(0, MAX_POR_DIA).map((ev, i) => {
                const t = getToneTokens(null, ev.tone || "neutral");
                return (
                  <CalChip
                    key={`${ev.req.id}-${i}`}
                    $cor={t.accent}
                    $bg={t.background}
                    title={`${ev.req.numero} · ${ev.req.cliente_nome || ""}${ev.titulo ? ` · ${ev.titulo}` : ""}`}
                    onClick={() => onAbrir(ev.req.id)}
                  >
                    <span className="num">{String(ev.req.numero || "").split("-").pop()}</span>
                    <span className="nome">{ev.rotulo || ev.req.cliente_nome || ev.req.numero}</span>
                  </CalChip>
                );
              })}
              {itens.length > MAX_POR_DIA ? (
                <div style={{ fontSize: 11, color: "var(--ink-4)" }}>+{itens.length - MAX_POR_DIA} mais</div>
              ) : null}
            </CalCell>
          );
        })}
      </CalGrid>
    </CalWrap>
  );
}
